/*
    Copyright (c) Computer-L.A.N. GmbH (http://www.computer-lan.de).
    Alle Rechte vorbehalten.
*/

import { useEffect } from "react";
import { TFunction } from "i18next";
import { useAppContext } from "../../Components/AppContext";
import IAppContext from "../../Components/AppContext/IAppContext";
import ViewModel from "../../ViewModels/ViewModel";
import { translationKeys, useTranslation } from "./../../Internationalisierung/i18nBasis";
import AppAboutDialogProps from "./AppAboutDialogProps";
import AppAboutDialogState, { createInitialState } from "./AppAboutDialogState";

export default class AppAboutDialogModel extends ViewModel<AppAboutDialogState> {
    private readonly _appContext: IAppContext;
    private readonly _t: TFunction;
    public constructor(private readonly _props: AppAboutDialogProps) {
        super({
            initialState: createInitialState(),
        });
        this._appContext = useAppContext();
        this._t = useTranslation().t;

        useEffect(
            () => {
                this.showLicenses();
            },
            [_props.isOpen]);
    }

    private async showLicenses(): Promise<void> {
        if (this._props.isOpen) {
            const licenses = await this.loadLicenses();
            this.setState(next => {
                next.licenses = licenses;
            });
        } else {
            this.reset();
        }
    }

    private async loadLicenses(): Promise<string> {
        const url = `${process.env.PUBLIC_URL}/licenses.txt?version=${this._appContext.options.appVersion}`;
        const response = await fetch(url);
        if (!response || !response.ok || !response.body) {
            return "";
        }

        const reader = response.body.getReader();
        const licenseData = await this.readLicenseData(reader);

        const decoder = new TextDecoder("utf-8");
        return decoder.decode(licenseData);
    }

    private async readLicenseData(reader: ReadableStreamDefaultReader<Uint8Array>): Promise<Uint8Array> {
        let licenseData = new Uint8Array(0);
        let done = false;
        do {
            const read = await reader.read();
            if (read.value) {
                const buffer = new Uint8Array(licenseData.length + read.value.length);
                buffer.set(licenseData);
                buffer.set(read.value, licenseData.length);
                licenseData = buffer;
            }

            done = read.done;
        }
        while (!done);

        return licenseData;
    }

    public get version(): string {
        return this._appContext.options.appVersion;
    }

    public get instanzId(): string {
        return this._appContext.options.instanzId;
    }

    public get isOpen(): boolean {
        return this._props.isOpen;
    }

    public get licenses(): string {
        if (this.state.licenses) {
            return this.state.licenses;
        }

        return this._t(translationKeys.appAboutDialog.loading);
    }

    public readonly ok = (): void => {
        this._props.onOk();
        this.reset();
    };
}
